import EndpointUrl from "@app/core/apiEndpoints/endPoints";
import { clearToken, getToken } from "./token-store";

/**
 * IIDM-3067 T1 — the ONE logout path of the staff app (profile menu, access-denied page, and the
 * loop-guard branch of the 401 re-authorize in pkce.ts — since T3 a plain 401 re-authorizes).
 *
 * Before: each of the three sites navigated to `${API}/logout?token=<bearer>&…`, putting the
 * bearer token into a URL — browser history, the API's access log, any proxy log — and cleared a
 * different, incomplete set of storage keys. Now:
 *
 *   1. revoke the token at the SSO with `POST /api/token/revoke` + `Authorization: Bearer`
 *      (IIDM-2978; same contract as iidm_platform `revokeSsoToken`) — the token never enters a URL;
 *   2. IIDM-3159: tell the BE to drop its cached copy of SSO's answer for this token
 *      (`POST /v1/auth/forget`, IIDM-3156). The BE caches the token check for up to 60 s
 *      (IIDM-3149), so without this the revoked token kept working that long. It runs AFTER the
 *      revoke: forgetting first would let a request still in flight re-cache a token the SSO has not
 *      yet revoked;
 *   3. clear every key the app writes;
 *   4. send the browser to the BE's PHASE-2 logout (`/logout?redirect&client_id&redirect_uri`),
 *      which forwards to the SSO's `dologout` so the SSO session cookie is cleared too. Phase 1
 *      (`?token=`) is skipped entirely, because step 1 already did its job.
 *
 * Never throws and never blocks on the SSO for more than REVOKE_TIMEOUT_MS: a slow or unreachable
 * SSO must not leave the user looking logged in.
 */
export const REVOKE_TIMEOUT_MS = 5000;

/**
 * Every localStorage key the staff app writes (grep of setLocalStorage/localStorage.setItem, and
 * the key map in docs/03_frontend_state_data_flow.md). The plan named the first seven; the rest are
 * the same user's working state (grid caches, filters, form drafts) and must not survive logout on a
 * shared machine either.
 */
export const APP_STORAGE_KEYS = [
  "token",
  "userPermission",
  "appUrl",
  "payload",
  "filter",
  "requestInfo",
  "gridResponse",
  "syncing",
  "Dashboardsyncing",
  "globalSearch",
  "pageNo",
  "filter_name",
  "soQuantity",
  "FormikArray",
  "selectedItems",
  "dropdownOptions",
  "salesDropdownOptions",
  "quoteData",
  "leftkey",
  "repairSelectAll",
  "part_purchase_form",
  "search",
];

/**
 * The SSO origin used for the revoke call. `REACT_APP_SSO_URL` when set;
 * otherwise derived from `REACT_APP_SERVER_URL`, which is the SSO's `…/oauth` base — the login
 * code exchange in src/auth/pkce.ts posts to `${REACT_APP_SERVER_URL}/token`. The workflows write
 * `REACT_APP_SSO_URL` since IIDM-3067 T3; an environment that has not set it yet gets this fallback.
 *
 * @return {string} origin without a trailing slash, or "" when neither is configured
 */
export function getSsoOrigin(): string {
  const explicit = (process.env.REACT_APP_SSO_URL || "").trim();
  if (explicit) {
    return explicit.replace(/\/+$/, "");
  }
  const server = (process.env.REACT_APP_SERVER_URL || "").trim();
  if (!server) {
    return "";
  }

  return server.replace(/\/+$/, "").replace(/\/oauth$/, "");
}

/**
 * Revokes one access token at the SSO. 401 means already invalid/expired/revoked — nothing to do.
 *
 * @param {string} accessToken the bearer token to revoke
 * @return {Promise<boolean>} true only when the SSO confirmed the revocation
 */
export async function revokeSsoToken(accessToken: string): Promise<boolean> {
  const sso = getSsoOrigin();
  if (!accessToken || !sso || typeof fetch !== "function") {
    if (accessToken && !sso) {
      console.error("IIDM-3067: no SSO origin configured — cannot revoke the token.");
    }
    return false;
  }
  const controller = typeof AbortController === "function" ? new AbortController() : undefined;
  const timer = controller ? setTimeout(() => controller.abort(), REVOKE_TIMEOUT_MS) : undefined;
  try {
    const res = await fetch(`${sso}/api/token/revoke`, {
      method: "POST",
      headers: { Authorization: `Bearer ${accessToken}`, Accept: "application/json" },
      signal: controller ? controller.signal : undefined,
    });
    if (res.status === 401) {
      return false;
    }
    if (!res.ok) {
      console.error("IIDM-3067: SSO token revoke returned", res.status);
      return false;
    }

    return true;
  } catch (err) {
    console.error("IIDM-3067: SSO token revoke failed", err);
    return false;
  } finally {
    if (timer) {
      clearTimeout(timer);
    }
  }
}

/** IIDM-3159: how long logout waits for the BE's forget call before moving on. */
export const FORGET_TIMEOUT_MS = 3000;

/**
 * IIDM-3159 — drops the BE's cached identity for this token (`POST /v1/auth/forget`, always 204),
 * so a token the SSO has just revoked stops working at once instead of after the 60 s cache.
 * Token in the Authorization header only. Never throws: a failure only means the old token lives
 * out the cache, which is no worse than before.
 *
 * @param {string} accessToken the bearer token being logged out
 * @return {Promise<void>} resolves once the BE answered, failed, or the timeout passed
 */
export async function forgetBeToken(accessToken: string): Promise<void> {
  const api = (process.env.REACT_APP_API_URL || "").trim();
  if (!accessToken || !api || typeof fetch !== "function") {
    return;
  }
  const controller = typeof AbortController === "function" ? new AbortController() : undefined;
  const timer = controller ? setTimeout(() => controller.abort(), FORGET_TIMEOUT_MS) : undefined;
  try {
    const res = await fetch(`${api}${EndpointUrl.forgetTokenApi}`, {
      method: "POST",
      headers: { Authorization: `Bearer ${accessToken}`, Accept: "application/json" },
      signal: controller ? controller.signal : undefined,
    });
    if (!res.ok) {
      console.error("IIDM-3159: BE token forget returned", res.status);
    }
  } catch (err) {
    console.error("IIDM-3159: BE token forget failed", err);
  } finally {
    if (timer) {
      clearTimeout(timer);
    }
  }
}

/**
 * The T3 PKCE/re-auth bookkeeping in sessionStorage (`pkce.ts` STATE_KEY, VERIFIER_KEY, RETRY_KEY,
 * REAUTH_AT_KEY — listed literally because pkce.ts imports this module). IIDM-3067 T2: cleared at
 * logout so a stale re-auth timestamp cannot turn the next user's first 401 into a logout.
 */
export const AUTH_SESSION_KEYS: readonly string[] = [
  "oauth_state",
  "pkce_verifier",
  "oauth_retry",
  "oauth_reauth_at",
];

/** Removes every app key from localStorage, and the token + PKCE bookkeeping from sessionStorage. */
export function clearAppStorage(): void {
  clearToken();
  APP_STORAGE_KEYS.forEach((key) => {
    try {
      window.localStorage.removeItem(key);
    } catch (e) {
      // ignore
    }
  });
  AUTH_SESSION_KEYS.forEach((key) => {
    try {
      window.sessionStorage.removeItem(key);
    } catch (e) {
      // ignore
    }
  });
}

/**
 * The BE phase-2 logout URL. Carries NO token. `REACT_APP_API_URL` + `logoutApi` is the same
 * concatenation the three call sites used before IIDM-3067.
 *
 * @return {string} the URL the browser is sent to
 */
export function buildLogoutUrl(): string {
  const query = new URLSearchParams({
    redirect: "",
    client_id: `${process.env.REACT_APP_CLIENT_ID}`,
    redirect_uri: `${process.env.REACT_APP_REDIRECT_URI}`,
  });

  return `${process.env.REACT_APP_API_URL}${EndpointUrl.logoutApi}?${query.toString()}`;
}

let inFlight: Promise<void> | null = null;

/**
 * Revoke → forget → clear → navigate. Concurrent callers (several requests 401 at once, or the menu and
 * the interceptor together) share one run.
 *
 * @return {Promise<void>} resolves after the navigation has been requested
 */
export function revokeAndLogout(): Promise<void> {
  if (inFlight) {
    return inFlight;
  }
  inFlight = (async () => {
    try {
      const accessToken = getToken() || legacyToken();
      if (accessToken) {
        await revokeSsoToken(accessToken);
        await forgetBeToken(accessToken);
      }
    } finally {
      clearAppStorage();
      window.location.href = buildLogoutUrl();
    }
  })();
  // A navigation normally unloads the page; if it does not (tests, a blocked navigation), let a
  // later logout run again instead of returning a stale settled promise forever.
  inFlight.then(
    () => {
      inFlight = null;
    },
    () => {
      inFlight = null;
    }
  );

  return inFlight;
}

/**
 * A token left in localStorage by a pre-IIDM-3067 build, so a user upgraded mid-session still has
 * it revoked rather than merely deleted.
 *
 * @return {string} the legacy token or ""
 */
function legacyToken(): string {
  try {
    return window.localStorage.getItem("token") || "";
  } catch (e) {
    return "";
  }
}
